// src/components/DocumentPreviewButtons.tsx
// "👁 View" / "🖨 Print" / "⬇ Download", used everywhere a patient document is
// offered (Consultation preview, Patient Records, Lab reports, the IP Case
// Sheet header, generated documents in ConsultationChat/DischargeSummaryChat/
// PrescriptionBuilder).
//
// View shows the document's TEXT, the same way PreviousConsultations.tsx
// shows a past consultation note — a popup rendering a stored string, not a
// file being opened. That text already exists for almost every document this
// app creates: `patient_documents.extracted_text` is populated once, at
// creation time, with the plain-text/markdown-lite source for every
// AI-generated document (Consultation Notes, Discharge Summary, OPD
// Prescription sheet, IP Case Sheet — the caller already holds that text
// before it ever becomes a .docx) and asynchronously by OCR for an uploaded
// scan/photo/PDF. `GET .../documents/:id/status` returns that column without
// touching the file's bytes at all, so viewing a document is a small text
// read, not a download — this is the "affordable" design: no new storage,
// just reading the column every document-creation path already writes.
//
// Exception: an uploaded image or PDF is always shown as the file itself —
// its OCR text is a rough transcription, so it sits behind "Show extracted
// text" instead of replacing the picture.
//
// Otherwise a file's actual bytes are fetched only as a fallback, for the documents
// that genuinely have no text yet (OCR still running, or none was ever
// extracted) — and only rendered inline for types a browser can actually
// display (PDF, image). Anything else, at any stage, offers Download rather
// than silently triggering it.
import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/Dialog';
import { renderLiteMarkdown } from '../lib/markdownLite';
import { fetchPatientDocumentBlob, getPatientDocumentStatus, openPatientDocument } from '../api';
import { printPatientDocument } from '../features/printing/api';
import { toast } from '../hooks/useToast';

const OCR_POLL_MS = 2500;
const OCR_POLL_ATTEMPTS = 8; // ~20s — generous for Tesseract/pdf-parse on a normal-sized scan, without polling forever

/** A type the browser can show directly (uploaded scans and photos, PDFs). */
function isViewableType(type: string | null | undefined): boolean {
  const t = (type || '').toLowerCase();
  return t.startsWith('image/') || t.startsWith('application/pdf');
}

type PreviewState =
  | { kind: 'loading' }
  | { kind: 'text'; text: string }
  | { kind: 'pending' } // OCR still running — text will land in extracted_text shortly
  | { kind: 'file'; url: string; contentType: string; filename: string; renderable: boolean }
  | { kind: 'error'; message: string };

export default function DocumentPreviewButtons({ uhid, docId, label = 'document', className }: {
  uhid: string;
  docId: number;
  /** What to call this document in the dialog title and error text, e.g. "Consultation Notes". */
  label?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<PreviewState>({ kind: 'loading' });
  const [ocrText, setOcrText] = useState<string | null>(null);
  const [showOcr, setShowOcr] = useState(false);
  const attemptRef = useRef(0);
  const [printing, setPrinting] = useState(false);

  // Direct Print (features/printing/api.ts): a generated document prints on the
  // letterhead from its source text, an uploaded PDF/image as the file itself —
  // straight to the print dialog, no new tab. One job at a time per document.
  const print = async () => {
    if (printing) return;
    setPrinting(true);
    try {
      const r = await printPatientDocument(uhid, docId);
      if (r?.fallback) toast({ title: 'Opened in a new tab', description: 'This browser could not print directly — print from the new tab.' });
    } catch (e) {
      toast({ title: `Could not print ${label}`, description: e instanceof Error ? e.message : String(e), variant: 'danger' });
    } finally {
      setPrinting(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let objectUrl: string | null = null;
    attemptRef.current = 0;
    setState({ kind: 'loading' });
    setOcrText(null);
    setShowOcr(false);

    const loadFile = () => {
      fetchPatientDocumentBlob(uhid, docId)
        .then(({ blob, contentType, filename }) => {
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          const renderable = isViewableType(contentType) && !contentType.startsWith('image/tiff');
          setState({ kind: 'file', url: objectUrl, contentType, filename, renderable });
        })
        .catch(e => { if (!cancelled) setState({ kind: 'error', message: e instanceof Error ? e.message : String(e) }); });
    };

    const checkStatus = () => {
      getPatientDocumentStatus(uhid, docId)
        .then(({ ocr_status, extracted_text, file_type }) => {
          if (cancelled) return;
          // An uploaded image or PDF is shown as itself — its OCR text is only a
          // rough transcription, offered behind "Show extracted text".
          if (isViewableType(file_type)) {
            setOcrText(extracted_text && extracted_text.trim() ? extracted_text : null);
            loadFile();
            return;
          }
          if (extracted_text && extracted_text.trim()) { setState({ kind: 'text', text: extracted_text }); return; }
          const stillRunning = ocr_status === 'pending' || ocr_status === 'processing';
          if (stillRunning && attemptRef.current < OCR_POLL_ATTEMPTS) {
            attemptRef.current += 1;
            setState({ kind: 'pending' });
            timer = setTimeout(checkStatus, OCR_POLL_MS);
            return;
          }
          // Nothing to show as text (extraction finished empty, failed, or gave
          // up polling) — fall back to the file itself.
          loadFile();
        })
        .catch(() => { if (!cancelled) loadFile(); });
    };

    checkStatus();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, uhid, docId]);

  return (
    <>
      <div className={className ?? 'flex gap-2'}>
        <button type="button" className="btn-ghost" style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => setOpen(true)}>👁 View</button>
        <button type="button" className="btn-ghost" style={{ padding: '5px 12px', fontSize: 12 }} disabled={printing} onClick={print} title={`Print ${label}`}>
          {printing ? '🖨 Printing…' : '🖨 Print'}
        </button>
        <button type="button" className="btn-success" style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => openPatientDocument(uhid, docId, { inline: false })}>⬇ Download</button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
            {state.kind === 'file' && <DialogDescription>{state.filename}</DialogDescription>}
          </DialogHeader>

          {state.kind === 'loading' && (
            <div className="flex items-center justify-center py-10 text-sm text-ink-muted">Loading…</div>
          )}
          {state.kind === 'pending' && (
            <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-ink-muted">
              <div>Extracting text from this document…</div>
              <div className="text-[11.5px]">This only takes a few seconds for a scanned upload.</div>
            </div>
          )}
          {state.kind === 'error' && (
            <div className="flex items-center justify-center py-10 text-sm text-danger-ink">Could not load {label}: {state.message}</div>
          )}
          {state.kind === 'text' && (
            <div className="rounded-[var(--radius)] border border-line bg-bg p-3 text-[13px] leading-relaxed text-ink">
              {renderLiteMarkdown(state.text)}
            </div>
          )}
          {state.kind === 'file' && ocrText && (
            <div className="flex justify-end">
              <button type="button" className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setShowOcr(v => !v)}>
                {showOcr ? 'Show file' : 'Show extracted text'}
              </button>
            </div>
          )}
          {state.kind === 'file' && showOcr && ocrText && (
            <div className="rounded-[var(--radius)] border border-line bg-bg p-3 text-[13px] leading-relaxed text-ink">
              {renderLiteMarkdown(ocrText)}
            </div>
          )}
          {state.kind === 'file' && !showOcr && state.renderable && state.contentType.startsWith('application/pdf') && (
            <iframe src={state.url} title={label} className="h-[70vh] w-full rounded-[var(--radius)] border border-line" />
          )}
          {state.kind === 'file' && !showOcr && state.renderable && state.contentType.startsWith('image/') && (
            <img src={state.url} alt={label} className="max-h-[70vh] w-full rounded-[var(--radius)] border border-line object-contain" />
          )}
          {state.kind === 'file' && !showOcr && !state.renderable && (
            <div className="flex flex-col items-center gap-3 py-10 text-center text-sm text-ink-muted">
              <div>This file type can't be previewed in the browser.</div>
              <button type="button" className="btn-success" style={{ padding: '6px 14px', fontSize: 12.5 }} onClick={() => openPatientDocument(uhid, docId, { inline: false })}>
                ⬇ Download {state.filename}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
